import React, { useEffect, useMemo, useState } from "react";
import { X, Search, Check, Loader2, Link2 } from "lucide-react";
import { useAlert } from "components/UI_components/Alert";
import { taskApi } from "api/tasks/taskApi";
import { linkDesignToTasks, fetchLinkedTasks } from "../../../../api/design/service";

const toArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw?.data?.results) return raw.data.results;
  if (raw?.data) return raw.data;
  if (raw?.results) return raw.results;
  return [];
};

export default function DesignTaskLinkSelector({
  show,
  onClose,
  designId,
  siteId,
  onLinked,
}) {
  const { addAlert } = useAlert();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState([]); // [{ id, name, groupId, groupName, status }]
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (!show) return;
    setSearchTerm("");
    setSelectedIds([]);

    let cancelled = false;
    (async () => {
      if (!siteId && siteId !== 0) {
        setTasks([]);
        return;
      }
      setLoading(true);
      try {
        const [groupsRaw, linkedTasksData] = await Promise.all([
          taskApi.getAllTaskGroupsBySite(siteId),
          fetchLinkedTasks(designId).catch(() => []),
        ]);
        const groups = toArray(groupsRaw);
        
        if (!cancelled) {
          setSelectedIds((linkedTasksData || []).map((t) => t.id));
        }

        const tasksByGroup = await Promise.all(
          groups.map(async (g) => {
            try {
              const list = await taskApi.getAllTasks(g.id);
              return (toArray(list) || []).map((t) => ({
                id: t.id,
                name: t.task_name || t.name || `Task #${t.id}`,
                status: t.task_status || t.status || "",
                groupId: g.id,
                groupName: g.group_name || g.task_group_name || `Group #${g.id}`,
              }));
            } catch {
              return [];
            }
          })
        );

        const flat = tasksByGroup.flat().filter((t) => t?.id);
        if (!cancelled) setTasks(flat);
      } catch (e) {
        console.error("Failed to fetch tasks for linking", e);
        addAlert(e?.message || "Failed to fetch tasks", "error");
        if (!cancelled) setTasks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, siteId, addAlert]);

  const filteredTasks = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((t) => {
      const n = String(t.name || "").toLowerCase();
      const g = String(t.groupName || "").toLowerCase();
      return n.includes(q) || g.includes(q);
    });
  }, [tasks, searchTerm]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of filteredTasks) {
      const key = t.groupName || "Tasks";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return Array.from(map.entries()).map(([groupName, list]) => ({
      groupName,
      list,
    }));
  }, [filteredTasks]);

  const toggleTask = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (!designId && designId !== 0) return;
    if (!selectedIds.length) {
      addAlert("Please select at least one task", "warning");
      return;
    }
    setSaving(true);
    try {
      await linkDesignToTasks(designId, selectedIds);
      addAlert("Design linked to task(s) successfully", "success");
      if (typeof onLinked === "function") {
        await onLinked((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            linked_task_ids: selectedIds,
          };
        });
      }
      onClose();
    } catch (e) {
      console.error("Failed to link design to tasks", e);
      addAlert(e?.message || "Failed to link design to tasks", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl w-full max-w-xl flex flex-col max-h-[80vh] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-sm-primary" />
              Link Design to Tasks
            </h3>
            <p className="text-xs text-gray-500">
              {selectedIds.length} selected
              {loading ? " • Loading tasks…" : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks or groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-100 border-none rounded-lg text-sm focus:ring-2 focus:ring-sm-primary/20 transition-all outline-none"
            />
          </div>
        </div>

        {/* List */}
        <div
          className="flex-1 overflow-y-auto p-3 min-h-[320px]"
          style={{ scrollbarWidth: "none" }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin mb-2" />
              <span className="text-xs font-medium">
                Loading task groups & tasks…
              </span>
            </div>
          ) : grouped.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-xs italic">
              No tasks found matching "{searchTerm}"
            </div>
          ) : (
            <div className="space-y-3">
              {grouped.map(({ groupName, list }) => (
                <div key={groupName} className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 border-b flex items-center justify-between">
                    <div className="text-xs font-semibold text-gray-700 truncate">
                      {groupName}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {list.length} task{list.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="divide-y">
                    {list.map((t) => {
                      const isSelected = selectedIds.includes(t.id);
                      return (
                        <button
                          type="button"
                          key={t.id}
                          onClick={() => toggleTask(t.id)}
                          className={`w-full text-left flex items-center gap-3 px-3 py-2.5 transition-colors ${
                            isSelected ? "bg-sm-primary/5" : "hover:bg-gray-50"
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded-md border-2 transition-all flex items-center justify-center ${
                              isSelected
                                ? "bg-sm-primary border-sm-primary"
                                : "border-gray-200"
                            }`}
                          >
                            {isSelected && (
                              <Check className="w-3.5 h-3.5 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {t.name}
                            </div>
                            <div className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">
                              {t.status || "Task"}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50/50 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading || selectedIds.length === 0}
            className="flex-1 bg-sm-primary text-white py-2 px-4 rounded-lg text-sm font-bold shadow-lg shadow-sm-primary/20 hover:bg-sm-primary/90 disabled:opacity-50 disabled:shadow-none transition-all flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Linking...
              </>
            ) : (
              "Link Selected Tasks"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


